import { useCallback, useEffect, useState } from 'react';
import { Card, Table, Badge, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { API_BASE } from 'apiConfig';
import SidePanel from 'pages/traffic/SidePanel';
import BlockCustomerButton from 'components/modules/chat/BlockCustomerButton';

interface BlockedVisitor {
  customer_id: string;
  name: string;
  email: string;
  country?: string;
  country_name?: string;
  region?: string;
  city?: string;
  ip?: string;
  browser?: string;
  os?: string;
  device?: string;
  history?: any[] | null;
  blocked_at?: string | null;
  last_seen?: string | null;
}

interface ChatMsg {
  id: string;
  message: string;
  who: 'agent' | 'customer';
  datemade: string;
  agent_name?: string;
}

const flagSrc = (c?: string) =>
  c ? `https://cdn.easychatdesk.com/flags/${c.toLowerCase()}.svg` : '';

const prettyCountry = (name?: string, code?: string) => {
  const n = (name || '').trim();
  if (n.length > 2) return n;
  const c = (n || code || '').trim();
  return c ? c.toUpperCase() : '';
};

const fmt = (s?: string | null) =>
  s ? new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';

const BlockedVisitors = () => {
  const token = useSelector((s: RootState) => s.auth.token);
  const [list, setList] = useState<BlockedVisitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<BlockedVisitor | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [transcript, setTranscript] = useState<ChatMsg[]>([]);
  const [transcriptLoading, setTranscriptLoading] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/customers/blocked`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = res.ok ? await res.json() : { customers: [] };
      setList(data.customers || []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const openVisitor = async (v: BlockedVisitor) => {
    setSelected(v);
    setPanelOpen(true);
    setTranscript([]);
    setTranscriptLoading(true);
    try {
      const res = await fetch(`${API_BASE}/chats/${v.customer_id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined
      });
      const rows = res.ok ? await res.json() : [];
      setTranscript(
        (Array.isArray(rows) ? rows : [])
          .filter((r: any) => r && r.message)
          .map((r: any) => ({
            id: String(r.id),
            message: r.message,
            who: r.who_sent_message === 'agent' ? 'agent' : 'customer',
            datemade: r.datemade,
            agent_name: r.agent_name || undefined
          }))
      );
    } catch {
      setTranscript([]);
    } finally {
      setTranscriptLoading(false);
    }
  };

  return (
    <div className="pb-6">
      <div className="mb-4">
        <h2 className="mb-2 text-body-emphasis">Blocked visitors</h2>
        <h5 className="text-body-tertiary fw-semibold">
          Visitors who can no longer see or use your chat widget
        </h5>
      </div>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" size="sm" /> Loading…
            </div>
          ) : list.length === 0 ? (
            <div className="text-center text-body-tertiary py-5">
              No blocked visitors. Block someone from the Traffic page or a chat.
            </div>
          ) : (
            <Table hover responsive className="fs-9 mb-0 align-middle">
              <thead>
                <tr className="text-body-tertiary">
                  <th>Visitor</th>
                  <th>Location</th>
                  <th>IP</th>
                  <th>Blocked</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map(v => (
                  <tr
                    key={v.customer_id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openVisitor(v)}
                  >
                    <td>
                      <div className="fw-semibold text-body-emphasis">
                        {v.name || 'Unnamed Customer'}
                      </div>
                      <div className="text-body-tertiary">{v.email || '—'}</div>
                    </td>
                    <td>
                      <span className="d-inline-flex align-items-center gap-2">
                        {v.country && <img src={flagSrc(v.country)} width={18} alt="" />}
                        {[v.city, prettyCountry(v.country_name, v.country)]
                          .filter(Boolean)
                          .join(', ') || '—'}
                      </span>
                    </td>
                    <td>{v.ip || '—'}</td>
                    <td>{fmt(v.blocked_at)}</td>
                    <td className="text-end" onClick={e => e.stopPropagation()}>
                      <BlockCustomerButton
                        customerId={v.customer_id}
                        blocked
                        onToggled={() =>
                          setList(prev => prev.filter(x => x.customer_id !== v.customer_id))
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <SidePanel show={panelOpen} onClose={() => setPanelOpen(false)} title="Blocked visitor">
        {selected && (
          <>
            <h4 className="mb-0">{selected.name || 'Unnamed Customer'}</h4>
            <p className="text-body-tertiary mb-1">{selected.email || '—'}</p>
            <p className="mb-1">
              {selected.country && (
                <img src={flagSrc(selected.country)} width={20} className="me-2" alt="" />
              )}
              {[selected.city, selected.region, prettyCountry(selected.country_name, selected.country)]
                .filter(Boolean)
                .join(', ') || '—'}
            </p>
            <div className="fs-9 text-body-tertiary mb-3">
              <div>IP: {selected.ip || '—'}</div>
              <div>Browser: {selected.browser || '—'} · OS: {selected.os || '—'}</div>
              <div>Blocked: {fmt(selected.blocked_at)}</div>
              <div>Last seen: {fmt(selected.last_seen)}</div>
            </div>
            <BlockCustomerButton
              customerId={selected.customer_id}
              blocked
              className="w-100 mb-4"
              onToggled={() => {
                setList(prev => prev.filter(x => x.customer_id !== selected.customer_id));
                setPanelOpen(false);
              }}
            />

            <h6 className="text-body-emphasis">Visited pages</h6>
            <ul className="ps-3 mb-4 fs-9">
              {selected.history && selected.history.length ? (
                selected.history.map((h: any, i: number) => (
                  <li key={i} className="mb-1">
                    <a href={h.visited_page} target="_blank" rel="noopener noreferrer">
                      {(h.title || h.visited_page || '').toString().slice(0, 60) || 'Page'}
                    </a>
                  </li>
                ))
              ) : (
                <li className="text-body-tertiary">No visits recorded</li>
              )}
            </ul>

            <h6 className="text-body-emphasis">Conversation history</h6>
            {transcriptLoading ? (
              <div className="text-body-tertiary fs-9">Loading…</div>
            ) : transcript.length ? (
              <div className="d-flex flex-column gap-2">
                {transcript.map(m => (
                  <div key={m.id}>
                    <Badge bg={m.who === 'agent' ? 'primary' : 'secondary'} className="mb-1">
                      {m.who === 'agent' ? m.agent_name || 'Agent' : 'Visitor'}
                    </Badge>
                    <div
                      className="fs-9 text-body-secondary"
                      dangerouslySetInnerHTML={{ __html: m.message }}
                    />
                    <div className="fs-10 text-body-tertiary">{fmt(m.datemade)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-body-tertiary fs-9">No conversation history</div>
            )}
          </>
        )}
      </SidePanel>
    </div>
  );
};

export default BlockedVisitors;
